import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import classNames from 'classnames'
import { AxiosError, type AxiosResponse } from 'axios'
import { debounce } from 'lodash'
import {
  makeTextFieldAutoFocus,
  makeTextFieldElement,
  makeTextFieldIcon,
  TextField,
} from 'shared/ui/TextField'
import { Button, type IButtonProps, Icon, SpinnerLoader } from 'shared/ui'
import { Error } from 'shared/ui/Error'
import { logger, validPhoneNumber } from 'shared/lib'
import {
  MAX_VALID_INTERNATIONAL_NUMBER_LENGTH,
  MAX_VALID_NUMBER_LENGTH,
  MIN_VALID_NUMBER_LENGTH,
} from 'shared/constants/limits'
import { ContactsApi } from 'entities/Contacts'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { type IParamsTestSMS, type IResponseTestSMS } from 'widgets/TestSMS/types/types'
import { TestSMSBtn } from 'widgets/TestSMS/ui/TestSMSBtn/TestSMSBtn'
import { type SendTestModalStore } from 'widgets/TestSMS/ui/TestSmsWithMergeField'
import styles from './styles.module.scss'

export type ITestSMSDefaultProps = {
  disabled?: boolean
  params: IParamsTestSMS
  request: (params: IParamsTestSMS) => Promise<AxiosResponse<IResponseTestSMS>>
  onToggleShowTest?: (value: boolean) => void
  isTopError?: boolean
  sendTestModalStore: SendTestModalStore
  isShowTest?: boolean
  setShowTest?: Dispatch<SetStateAction<boolean>>
  testSMSButtonProps?: IButtonProps
}

export const TestSMSDefault = ({
  disabled,
  params,
  request,
  onToggleShowTest,
  isTopError,
  sendTestModalStore,
  testSMSButtonProps,
  isShowTest: isShowTestProps,
  setShowTest: setShowTestProps,
}: ITestSMSDefaultProps) => {
  const [isShowTestLocal, setShowTestLocal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isNumberValidated, setIsNumberValidated] = useState(false)
  const [number, setNumber] = useState('')
  const [completed, setCompleted] = useState(false)
  const [error, setError] = useState<ReactNode>('')

  const isShowTest = isShowTestProps ?? isShowTestLocal
  const setShowTest = setShowTestProps ?? setShowTestLocal

  useEffect(() => {
    onToggleShowTest?.(isShowTest)
  }, [isShowTest])

  useEffect(() => {
    return () => {
      setNumber('')
      setCompleted(false)
      setError('')
      onToggleShowTest?.(false)
    }
  }, [isShowTest])

  const btnContent = useMemo(() => {
    if (completed) {
      return <Icon icon={'check1'} />
    }
    if (isLoading) {
      return <SpinnerLoader ariaLabel={'TestSMSDefault'} size={15} />
    }
    return 'Send test'
  }, [isLoading, completed])

  const onShowTest = () => {
    setShowTest(true)
  }

  const onCancel = () => {
    setShowTest(false)
    setCompleted(false)
  }

  const validateNumber = async (number: string) => {
    try {
      setIsLoading(true)
      const { data: isValid } = await ContactsApi.validateNumber({ number })

      if (!isValid) {
        setError('Enter a valid phone number')
      }
    } catch (e) {
      logger.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  const debounceValidateNumber = useCallback(debounce(validateNumber, 1000), [])

  const handleValidateNumber = async (phoneNumber: string) => {
    debounceValidateNumber(phoneNumber)
  }

  const onChangeValue = (value: string) => {
    const hasPlusSign = value.startsWith('+')
    let correctFormatNumber = value.replace(/\D/g, '')

    if (hasPlusSign) {
      correctFormatNumber = '+' + correctFormatNumber
    }
    const numberLength = correctFormatNumber.length
    const maxNumberLength = correctFormatNumber.includes('+')
      ? MAX_VALID_INTERNATIONAL_NUMBER_LENGTH
      : MAX_VALID_NUMBER_LENGTH

    if (numberLength > maxNumberLength) return

    if (
      correctFormatNumber.length >= MIN_VALID_NUMBER_LENGTH &&
      correctFormatNumber.length <= maxNumberLength
    ) {
      setIsNumberValidated(true)
      handleValidateNumber(correctFormatNumber)
    }
    if (numberLength < MIN_VALID_NUMBER_LENGTH && isNumberValidated) {
      setIsNumberValidated(false)
    }

    setNumber(correctFormatNumber)
  }

  const onTest = async () => {
    if (isLoading || completed) return
    if (!validPhoneNumber(number)) {
      setError('Please enter a valid phone number to test the message')
    } else {
      try {
        setIsLoading(true)
        const { data } = await request({
          ...params,
          number,
        })
        if (data?.status === 'success') {
          setCompleted(true)
          setTimeout(onCancel, 1000)
        } else {
          if (data.message === 'Insufficient balance to send this message') {
            setError(
              <>
                Not enough credits to test the message.
                <Button
                  text={'Buy credits'}
                  typeBtn={'link'}
                  size={'small'}
                  contained={'primary'}
                  onClick={() => {
                    sendTestModalStore.buyCreditsModalStore.openModal()
                  }}
                />
              </>
            )
          } else {
            setError(data.message)
          }
        }
      } catch (e) {
        if (e instanceof AxiosError) {
          setError(e.errors.getError('number'))
          if (e.errors.getError('message') === 'This feature is restricted')
            new FeatureRestrictedModalStore('can_create_contacts')
        }
        logger.error(e)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const onFocus = () => {
    setError('')
  }

  const errorContent = error && (
    <Error
      type='text'
      error={error}
      wrapClassName={classNames(styles.error, { [styles.isTopError]: isTopError })}
    />
  )

  return (
    <div>
      {isShowTest ? (
        <>
          {isTopError && errorContent}
          <TextField
            className={styles.textField}
            disabled={isLoading || completed}
            size={'small'}
            variant={'stroke'}
            InputProps={{ placeholder: 'Enter number' }}
            value={number}
            error={error}
            onFocus={onFocus}
            onChange={onChangeValue}
            mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
            leftActions={[
              makeTextFieldIcon({
                icon: 'phone2',
                margin: 0,
              }),
            ]}
            rightActions={[
              makeTextFieldElement({
                element: completed ? null : (
                  <Button
                    typeBtn={'text'}
                    text={'Cancel'}
                    onClick={onCancel}
                    size={'small'}
                    className={styles.inputAction}
                    disabled={isLoading}
                  />
                ),
              }),
              makeTextFieldElement({
                element: (
                  <Button
                    text={btnContent}
                    onClick={onTest}
                    contained={'primary'}
                    size={'small'}
                    className={classNames(styles.inputAction, styles.send)}
                    disabled={disabled}
                  />
                ),
              }),
            ]}
          />
          {!isTopError && errorContent}
        </>
      ) : (
        <TestSMSBtn onClick={onShowTest} disabled={disabled} {...testSMSButtonProps} />
      )}
    </div>
  )
}
